import { useState } from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import {
  createDateFromHHMM,
  formatDateHHMM,
  createDateFromDDMMYYYY,
  createDateFromDateAndTimeObjects,
  formatDate,
  getDateIsValid,
} from "@icarius-utils/date";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { subDays, isWithinInterval, isSameDay, differenceInDays, differenceInMinutes } from "date-fns";

const useHandleForm = (data, createCallback, modifyCallback, openCalendarList, employeeList) => {

  const isCreate = !Boolean(data);
  const hasOriginalEntryDate = Boolean(data?.["FECHA DE ENTRADA"]);
  const hasOriginalExitDate = Boolean(data?.["FECHA DE SALIDA"]);
  const isIncomplete = isCreate ? false : !(hasOriginalEntryDate && hasOriginalExitDate); // si tiene datos pero no todos

  const createInitialData = () => {

    const getInitialFieldData = (fieldName) => {
      if (fieldName === "employee") {
        if (isCreate) return null;
        return employeeList.find((item) => item.employeeCode === data["CODIGO DE EMPLEADO"]);
      }

      if (fieldName === "entryDate") {
        if (isCreate) return null;
        if (!hasOriginalEntryDate) return subDays(createDateFromDDMMYYYY(data["FECHA DE SALIDA"]), 1);
        return createDateFromDDMMYYYY(data["FECHA DE ENTRADA"])
      }

      if (fieldName === "entryTime") {
        if (isCreate || !hasOriginalEntryDate) return null;
        return createDateFromHHMM(data["HORA DE ENTRADA"]);
      }

      if (fieldName === "exitDate") {
        if (isCreate) return null;
        if (!hasOriginalExitDate) return createDateFromDDMMYYYY(data["FECHA DE ENTRADA"]);
        return createDateFromDDMMYYYY(data["FECHA DE SALIDA"]);
      }

      if (fieldName === "exitTime") {
        if (isCreate || !hasOriginalExitDate) return null;
        return createDateFromHHMM(data["HORA DE SALIDA"]);
      }

      if (fieldName === "comment") {
        if (isCreate) return "";
        return data["COMENTARIOS"];
      }

      return "";
    }

    const fieldNames = [
      "employee",
      "entryDate",
      "entryTime",
      "exitDate",
      "exitTime",
      "comment",
    ];

    return fieldNames.reduce((initialData, fieldName) => {
      return ({
        ...initialData,
        [fieldName]: getInitialFieldData(fieldName)
      })
    }, {});
  }

  const [formData, setFormData] = useState(createInitialData());

  const {
    employee,
    entryDate,
    entryTime,
    exitDate,
    exitTime,
    comment,
  } = formData;

  const dispatch = useDispatch();

  const validateSomeCalendarIncludesDate = (date) => {
    const calendarsToUse = openCalendarList.filter((item) => item.type === employee.calendarType);

    // setear tiempo en 0 para que la comparacion funcione bien
    const dateToValidate = date;
    dateToValidate.setHours(0);
    dateToValidate.setMinutes(0);
    dateToValidate.setSeconds(0);
    dateToValidate.setMilliseconds(0);

    return calendarsToUse.some((calendar) => {
      const dateFrom = createDateFromDDMMYYYY(calendar.from);
      const dateTo = createDateFromDDMMYYYY(calendar.to);

        return (
        isWithinInterval(dateToValidate, { start: dateFrom, end: dateTo })
        || isSameDay(dateToValidate, dateFrom)
        || isSameDay(dateToValidate, dateTo)
      );
    });
  }

  const submit = () => {
    if (isCreate && !employee) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectAnEmployee"), severity: "error" }));
      return;
    }

    if (!getDateIsValid(entryDate) || !getDateIsValid(exitDate) || !getDateIsValid(entryTime) || !getDateIsValid(exitTime)) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error" }));
      return;
    }

    const entryDateTime = createDateFromDateAndTimeObjects(entryDate, entryTime);
    const exitDateTime = createDateFromDateAndTimeObjects(exitDate, exitTime);

    // Verifico que las dos fechas sean días contiguos
    if (differenceInDays(exitDateTime, entryDateTime) > 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("entryAndExitDaysMustBeContiguous"), severity: "error", duration: 10000 }));
      return;
    }

    // Verifico que la fecha de salida sea mayor o igual que la de entrada
    if (differenceInMinutes(exitDateTime, entryDateTime) < 0) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("exitTimeMustBeGreaterThanOrEqualToEntry"), severity: "error", duration: 10000 }));
      return;
    }

    const dataToSend = {
      comment,
      entryDate: formatDate(entryDate),
      entryTime: formatDateHHMM(entryTime),
      exitDate: formatDate(exitDate),
      exitTime: formatDateHHMM(exitTime),
    }

    if (isCreate) {
      dataToSend.employeeCode = employee.employeeCode;
    } else {
      dataToSend.internalCode = data.internalCode;
    }

    isCreate ? createCallback(dataToSend) : modifyCallback(dataToSend);
  }

  const setFormValue = (value, fieldName) => {
    if (fieldName === "employee") {
      // validar que si se setea empleado, este tenga algun calendario abierto
      if (value && !openCalendarList.some((item) => item.type === value.calendarType)) {
        dispatch(openSnackbarAction({ msg: getLocalizedString("employeeHasNoOpenCalendar", { name: value.employeeName }), severity: "error" }));
        return;
      }

      setFormData({
        ...formData,
        [fieldName]: value,
        // limpiar campos porque cambia calendario
        entryDate: null,
        entryTime: null,
        exitDate: null,
        exitTime: null,
      });
      return;
    }

    if (fieldName === "entryDate") {
      // verifico que si se setea fecha de entrada, esté dentro de alguno de los posibles calendarios abiertos
      if (value && getDateIsValid(value) && !validateSomeCalendarIncludesDate(value)) {
        const openCalendarsToUse = openCalendarList.filter((item) => item.type === employee.calendarType);
        const dateRangesString = openCalendarsToUse.map((item) => `del ${item.from} al ${item.to}`).join(", o ");

        dispatch(openSnackbarAction({
          msg: `${getLocalizedString("assistanceDateMustFitOpenCalendar")}: ${dateRangesString}`,
          severity: "error",
          duration: null,
        }));
        return;
      }

      setFormData({
        ...formData,
        [fieldName]: value,
        entryTime: null,
        exitDate: null,
        exitTime: null,
      });
      return;
    }

    setFormData({
      ...formData,
      [fieldName]: value,
    });
  }

  return {
    formData,
    isCreate,
    hasOriginalEntryDate,
    isIncomplete,
    setFormValue,
    submit,
  }
}

export default useHandleForm;